import React from 'react';
import { SetAsideType } from '../types';
import { SET_ASIDES } from '../lib/constants';
import * as Icons from 'lucide-react';

interface SetAsideIconProps {
  type: SetAsideType;
  size?: number;
  className?: string;
  showTooltip?: boolean;
}

// Helper function to map SAM.gov set-aside names to our internal types
function normalizeSetAsideType(type: string): string | null {
  const normalizedType = type.toLowerCase();
  
  // Direct matches
  if (SET_ASIDES[type as keyof typeof SET_ASIDES]) {
    return type;
  }
  
  // Pattern matching for common SAM.gov variations
  if (normalizedType.includes('8(a)') || normalizedType.includes('8a')) {
    return '8(a)';
  }
  if (normalizedType.includes('hubzone') || normalizedType.includes('hub zone')) {
    return 'HUBZone';
  }
  if (normalizedType.includes('service-disabled veteran') || normalizedType.includes('sdvo')) {
    return 'SDVOSB';
  }
  if (normalizedType.includes('veteran') || normalizedType === 'vosb' || normalizedType === 'vsa' || normalizedType === 'vss') {
    return 'VOSB';
  }
  if (normalizedType.includes('women-owned') && normalizedType.includes('economically disadvantaged')) {
    return 'EDWOSB';
  }
  if (normalizedType.includes('women-owned')) {
    return 'WOSB';
  }
  if (normalizedType.includes('small disadvantaged')) {
    return 'SDB';
  }
  if (normalizedType.includes('small business') || normalizedType.includes('small enterprise')) {
    return 'SB';
  }
  
  return null;
}

export function SetAsideIcon({ type, size = 20, className = '', showTooltip = true }: SetAsideIconProps) {
  // Try to normalize the set-aside type
  const normalizedType = normalizeSetAsideType(type);
  const setAside = normalizedType ? SET_ASIDES[normalizedType as keyof typeof SET_ASIDES] : null;
  
  // If we can't find a matching set-aside, use a default
  if (!setAside) {
    return (
      <div className="relative group">
        <Icons.Award 
          size={size} 
          className={`text-bw-muted ${className}`}
        />
        {showTooltip && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-10">
            <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap max-w-xs">
              {type}
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 top-full -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const IconComponent = Icons[setAside.icon as keyof typeof Icons] as React.ComponentType<any>;

  return (
    <div className="relative group">
      <IconComponent 
        size={size} 
        className={`${setAside.color} ${className}`}
      />
      {showTooltip && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-10">
          <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap max-w-xs">
            {setAside.name}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full -mt-1">
            <div className="border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
}